import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import DataEditor, {
  GridCellKind,
  GridColumn,
  Item,
  GridCell,
} from '@glideapps/glide-data-grid';
import {
  ProspectsGridProps,
  GetProspectsResponse,
  ProspectGridItem,
  GetDncListResponse,
  DncProspectItem,
  CrmEvents,
} from '@dreamai/types';
import api from '../../../../api';
import { usePusher } from '../../../../contexts/PusherContext';
import { ReactComponent as DownloadIcon } from '../../../../icons/download.svg';
import CreateCampaignModal from '../../ExpansiveSideNavigation/CreateCampaignModal';
import '@glideapps/glide-data-grid/dist/index.css';
import './crm-grid.scss';

interface ProspectsGridComponentProps extends ProspectsGridProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
}

type TabType = 'all' | 'dnc';

const ProspectsGrid: React.FC<ProspectsGridComponentProps> = ({
  threadId,
  updateThreadViewVisualDescription,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const [prospects, setProspects] = useState<ProspectGridItem[]>([]);
  const [dncProspects, setDncProspects] = useState<DncProspectItem[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [loading, setLoading] = useState(true);
  const [dncLoading, setDncLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [sortConfig, setSortConfig] = useState<{
    colIndex: number;
    direction: 'asc' | 'desc';
  } | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDimensions({ width, height });
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [loading]);

  const fetchProspects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetProspectsResponse>(
        '/crm-data/prospects',
      );

      if (response.data.success) {
        setProspects(response.data.prospects);
      } else {
        setError(response.data.error || 'Failed to load prospects');
      }
    } catch (err) {
      console.error('Error fetching prospects:', err);
      setError('Failed to load prospects');
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  const fetchDncProspects = useCallback(async () => {
    try {
      setDncLoading(true);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetDncListResponse>(
        '/crm-data/dnc?type=prospects',
      );

      if (response.data.success && response.data.prospects) {
        setDncProspects(response.data.prospects);
      }
    } catch (err) {
      console.error('Error fetching DNC prospects:', err);
    } finally {
      setDncLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchProspects();
    fetchDncProspects();
  }, [fetchProspects, fetchDncProspects]);

  // Listen for prospect created events to refresh the grid
  useEffect(() => {
    if (!pusherChannel) return;

    const handleProspectCreated = () => {
      fetchProspects();
    };

    pusherChannel.bind(CrmEvents.PROSPECT_CREATED, handleProspectCreated);

    return () => {
      pusherChannel.unbind(CrmEvents.PROSPECT_CREATED, handleProspectCreated);
    };
  }, [pusherChannel, fetchProspects]);

  // Filter and search prospects
  const filteredProspects = useMemo(() => {
    let result = prospects;

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.email?.toLowerCase().includes(query) ||
          p.jobTitle?.toLowerCase().includes(query) ||
          p.companyName?.toLowerCase().includes(query),
      );
    }

    // Apply sorting
    if (sortConfig) {
      const { colIndex, direction } = sortConfig;
      result = [...result].sort((a, b) => {
        let aVal = '';
        let bVal = '';
        switch (colIndex) {
          case 0:
            aVal = a.name;
            bVal = b.name;
            break;
          case 1:
            aVal = a.email || '';
            bVal = b.email || '';
            break;
          case 2:
            aVal = a.jobTitle || '';
            bVal = b.jobTitle || '';
            break;
          case 3:
            aVal = a.companyName || '';
            bVal = b.companyName || '';
            break;
        }
        const aStr = aVal.toLowerCase();
        const bStr = bVal.toLowerCase();
        if (aStr < bStr) return direction === 'asc' ? -1 : 1;
        if (aStr > bStr) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [prospects, searchQuery, sortConfig]);

  const filteredDncProspects = useMemo(() => {
    let result = dncProspects;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (d) =>
          d.prospectName.toLowerCase().includes(query) ||
          d.prospectEmail?.toLowerCase().includes(query) ||
          d.campaignName.toLowerCase().includes(query) ||
          d.reason?.toLowerCase().includes(query),
      );
    }

    return result;
  }, [dncProspects, searchQuery]);

  // Define columns for DataEditor
  const columns: GridColumn[] = useMemo(
    () => [
      {
        title: `Name${sortConfig?.colIndex === 0 ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}`,
        id: 'name',
        width: 200,
      },
      {
        title: `Email${sortConfig?.colIndex === 1 ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}`,
        id: 'email',
        width: 250,
      },
      {
        title: `Job Title${sortConfig?.colIndex === 2 ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}`,
        id: 'jobTitle',
        width: 200,
      },
      {
        title: `Company${sortConfig?.colIndex === 3 ? (sortConfig.direction === 'asc' ? ' ↑' : ' ↓') : ''}`,
        id: 'companyName',
        width: 200,
      },
    ],
    [sortConfig],
  );

  const dncColumns: GridColumn[] = useMemo(
    () => [
      { title: 'Name', id: 'prospectName', width: 200 },
      { title: 'Email', id: 'prospectEmail', width: 250 },
      { title: 'Campaign', id: 'campaignName', width: 200 },
      { title: 'Reason', id: 'reason', width: 250 },
      { title: 'Added', id: 'createdAt', width: 120 },
    ],
    [],
  );

  const onHeaderClicked = useCallback((colIndex: number) => {
    setSortConfig((prev) => {
      if (prev?.colIndex === colIndex) {
        if (prev.direction === 'asc') {
          return { colIndex, direction: 'desc' };
        }
        return null;
      }
      return { colIndex, direction: 'asc' };
    });
  }, []);

  // Get cell content for DataEditor
  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const prospect = filteredProspects[row];
      if (!prospect) {
        return {
          kind: GridCellKind.Text,
          data: '',
          displayData: '',
          allowOverlay: false,
        };
      }

      switch (col) {
        case 0:
          return {
            kind: GridCellKind.Text,
            data: prospect.name,
            displayData: prospect.name,
            allowOverlay: false,
          };
        case 1:
          return {
            kind: GridCellKind.Uri,
            data: prospect.email ? `mailto:${prospect.email}` : '',
            displayData: prospect.email || '',
            allowOverlay: false,
          };
        case 2:
          return {
            kind: GridCellKind.Text,
            data: prospect.jobTitle || '',
            displayData: prospect.jobTitle || '',
            allowOverlay: false,
          };
        case 3:
          return {
            kind: GridCellKind.Text,
            data: prospect.companyName || '',
            displayData: prospect.companyName || '',
            allowOverlay: false,
          };
        default:
          return {
            kind: GridCellKind.Text,
            data: '',
            displayData: '',
            allowOverlay: false,
          };
      }
    },
    [filteredProspects],
  );

  const getDncCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const dnc = filteredDncProspects[row];
      if (!dnc) {
        return {
          kind: GridCellKind.Text,
          data: '',
          displayData: '',
          allowOverlay: false,
        };
      }

      switch (col) {
        case 0:
          return {
            kind: GridCellKind.Text,
            data: dnc.prospectName,
            displayData: dnc.prospectName,
            allowOverlay: false,
          };
        case 1:
          return {
            kind: GridCellKind.Text,
            data: dnc.prospectEmail || '',
            displayData: dnc.prospectEmail || '',
            allowOverlay: false,
          };
        case 2:
          return {
            kind: GridCellKind.Text,
            data: dnc.campaignName,
            displayData: dnc.campaignName,
            allowOverlay: false,
          };
        case 3:
          return {
            kind: GridCellKind.Text,
            data: dnc.reason || '',
            displayData: dnc.reason || '',
            allowOverlay: false,
          };
        case 4:
          return {
            kind: GridCellKind.Text,
            data: dnc.createdAt,
            displayData: dnc.createdAt
              ? new Date(dnc.createdAt).toLocaleDateString()
              : '',
            allowOverlay: false,
          };
        default:
          return {
            kind: GridCellKind.Text,
            data: '',
            displayData: '',
            allowOverlay: false,
          };
      }
    },
    [filteredDncProspects],
  );

  const handleExport = useCallback(() => {
    let headers: string[];
    let rows: string[][];
    let filename: string;

    if (activeTab === 'dnc') {
      headers = ['Name', 'Email', 'Campaign', 'Reason', 'Added'];
      rows = filteredDncProspects.map((d) => [
        d.prospectName,
        d.prospectEmail || '',
        d.campaignName,
        d.reason || '',
        d.createdAt ? new Date(d.createdAt).toLocaleDateString() : '',
      ]);
      filename = 'dnc-prospects.csv';
    } else {
      headers = ['Name', 'Email', 'Job Title', 'Company'];
      rows = filteredProspects.map((p) => [
        p.name,
        p.email || '',
        p.jobTitle || '',
        p.companyName || '',
      ]);
      filename = 'prospects.csv';
    }

    const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }, [activeTab, filteredProspects, filteredDncProspects]);

  if (loading) {
    return (
      <div className='crm-grid'>
        <div className='grid-loading-skeleton'>
          <div className='skeleton-header'>
            <div className='skeleton-header-cell skeleton-header-cell--narrow' />
            <div className='skeleton-header-cell skeleton-header-cell--first' />
            <div className='skeleton-header-cell skeleton-header-cell--second' />
          </div>
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className='skeleton-row'>
              <div className='skeleton-cell skeleton-cell--narrow' />
              <div className='skeleton-cell'>
                <div className='skeleton-circle' />
              </div>
              <div className='skeleton-cell' />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='crm-grid'>
        <div className='error'>{error}</div>
      </div>
    );
  }

  // Show empty state without toolbar when no data from backend (after loading complete)
  if (!loading && prospects.length === 0) {
    return (
      <div className='empty-state-wrapper-for-grid'>
        <div className='grid-empty-state'>
          <img
            src='/campaigns/nothing-in-grid.png'
            alt='No prospects'
            className='empty-state-image'
          />
          <h3 className='empty-state-title'>
            I'll add prospects here as we find them
          </h3>
          <p className='empty-state-description'>
            Once your campaigns start prospecting you'll find a list of all
            prospects here.
          </p>
          <button
            className='empty-state-button'
            onClick={() => setShowCampaignModal(true)}
          >
            Create a Campaign
          </button>
          <CreateCampaignModal
            show={showCampaignModal}
            onClose={() => setShowCampaignModal(false)}
          />
        </div>
      </div>
    );
  }

  const renderGrid = () => {
    if (activeTab === 'all') {
      if (
        dimensions.width > 0 &&
        dimensions.height > 0 &&
        filteredProspects.length > 0
      ) {
        return (
          <DataEditor
            columns={columns}
            rows={filteredProspects.length}
            getCellContent={getCellContent}
            getCellsForSelection={true}
            width={dimensions.width}
            height={dimensions.height}
            rowMarkers='number'
            smoothScrollX
            smoothScrollY
            onHeaderClicked={onHeaderClicked}
          />
        );
      }
      return (
        <div className='empty-state-wrapper-for-grid'>
          <div className='grid-empty-state'>
            <img
              src='/campaigns/nothing-in-grid.png'
              alt='No results'
              className='empty-state-image'
            />
            <h3 className='empty-state-title'>
              There aren't any prospects that match
            </h3>
            <p className='empty-state-description'>
              Change or remove your filters to see results.
            </p>
          </div>
        </div>
      );
    }

    // DNC tab
    if (dncLoading) {
      return <div className='grid-loading'>Loading...</div>;
    }

    if (
      dimensions.width > 0 &&
      dimensions.height > 0 &&
      filteredDncProspects.length > 0
    ) {
      return (
        <DataEditor
          columns={dncColumns}
          rows={filteredDncProspects.length}
          getCellContent={getDncCellContent}
          getCellsForSelection={true}
          width={dimensions.width}
          height={dimensions.height}
          rowMarkers='number'
          smoothScrollX
          smoothScrollY
        />
      );
    }

    return (
      <div className='empty-state-wrapper-for-grid'>
        <div className='grid-empty-state'>
          <img
            src='/campaigns/nothing-in-grid.png'
            alt='No DNC entries'
            className='empty-state-image'
          />
          <h3 className='empty-state-title'>
            No prospects on the Do Not Contact list
          </h3>
          <p className='empty-state-description'>
            Prospects added to DNC will appear here.
          </p>
        </div>
      </div>
    );
  };

  return (
    <div className='crm-grid'>
      {dncProspects.length > 0 && (
        <div className='grid-tabs'>
          <button
            className={`grid-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All prospects <span className='tab-count'>{prospects.length}</span>
          </button>
          <button
            className={`grid-tab ${activeTab === 'dnc' ? 'active' : ''}`}
            onClick={() => setActiveTab('dnc')}
          >
            Do not contact{' '}
            <span className='tab-count'>{dncProspects.length}</span>
          </button>
        </div>
      )}

      <div className='toolbar'>
        <div className='search'>
          <span className='search-label'>
            Search {activeTab === 'all' ? 'Prospects' : 'DNC List'}
          </span>
          <input
            type='text'
            className='search-input'
            placeholder=''
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className='actions'>
          <button className='export-btn' onClick={handleExport}>
            <DownloadIcon />
          </button>
        </div>
      </div>

      <div
        className='grid-container'
        ref={containerRef}
        style={{
          width: '100%',
          flex: '1 1 0',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {renderGrid()}
      </div>
    </div>
  );
};

export default ProspectsGrid;
